import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useResource as useCompany } from "@/hooks/useResource";
import { fmtNumber } from "@/lib/format";
import { planCode, productTypeLabel, sectionBucket } from "@/lib/production";
import { Printer, Download } from "lucide-react";

export default function ProductionVariance() {
  const { data, isLoading } = useResource("/production-plans");
  const { data: company } = useCompany("/settings/company");
  const plans = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const tol = Number(company?.costVarianceTolerancePct) || 5;

  const [date, setDate] = useState("");
  const [product, setProduct] = useState("");
  const [type, setType] = useState("All");
  const [statusF, setStatusF] = useState("All");

  const rows = useMemo(() => {
    const out = [];
    plans.forEach((p) => {
      (p.lines || []).forEach((l) => {
        const planned = Number(l.plannedQty) || 0;
        const hasActual = l.actualQty != null;
        const actual = hasActual ? Number(l.actualQty) : null;
        const varQty = hasActual ? actual - planned : null;
        const varPct = hasActual && planned ? (varQty / planned) * 100 : null;
        let status = "Pending";
        if (hasActual) status = Math.abs(varPct || 0) <= tol ? "On Plan" : (varPct > 0 ? "Above Plan" : "Below Plan");
        out.push({
          plan: planCode(p), date: p.date, section: p.section, planStatus: p.status,
          productId: l.productId, productName: l.productName, productType: l.productType || p.section,
          recipeVersion: l.recipeVersion, planned, actual, varQty, varPct, status,
        });
      });
    });
    return out
      .filter((r) => !date || r.date === date)
      .filter((r) => !product || `${r.productId} ${r.productName}`.toLowerCase().includes(product.toLowerCase()))
      .filter((r) => type === "All" || sectionBucket(r.productType) === sectionBucket(type))
      .filter((r) => statusF === "All" || r.status === statusF)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [plans, date, product, type, statusF, tol]);

  const exportCsv = () => {
    const head = ["Plan", "Date", "Product ID", "Product", "Recipe", "Planned", "Actual", "Variance Qty", "Variance %", "Status"];
    const body = rows.map((r) => [r.plan, r.date, r.productId, r.productName, `v${r.recipeVersion}`, r.planned, r.actual ?? "", r.varQty ?? "", r.varPct == null ? "" : r.varPct.toFixed(1), r.status]);
    const csv = [head, ...body].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "production-variance.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const toneFor = (s) => s === "On Plan" ? "text-emerald-600" : s === "Above Plan" ? "text-sky-600" : s === "Below Plan" ? "text-rose-600" : "text-stone-400";

  return (
    <div className="space-y-5">
      <PageHeader title="End-of-Day Production Variance" subtitle="Planned vs actual production · shared ERP">
        <div className="flex gap-2">
          <Button data-testid="variance-print" variant="outline" className="gap-1.5" onClick={() => window.print()}><Printer className="h-4 w-4" /> Print</Button>
          <Button data-testid="variance-export" variant="outline" className="gap-1.5" onClick={exportCsv}><Download className="h-4 w-4" /> Export</Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div><Label className="text-xs">Production Date</Label><Input type="date" data-testid="variance-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" /></div>
        <div><Label className="text-xs">Product</Label><Input data-testid="variance-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Search product" className="h-9 w-48" /></div>
        <div><Label className="text-xs">Type</Label>
          <select data-testid="variance-type" value={type} onChange={(e) => setType(e.target.value)} className="h-9 w-36 rounded-md border border-stone-200 bg-white px-2 text-sm">
            {["All", "Bakery", "Other"].map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div><Label className="text-xs">Status</Label>
          <select data-testid="variance-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="h-9 w-40 rounded-md border border-stone-200 bg-white px-2 text-sm">
            {["All", "Pending", "On Plan", "Above Plan", "Below Plan"].map((s) => <option key={s}>{s}</option>)}
          </select></div>
      </div>

      {isLoading ? <LoadingState label="Loading variance…" /> : rows.length === 0 ? (
        <EmptyState title="No production lines" message="Create plans and enter actual production to see variance." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Product</th><th className="px-4 py-2.5">Recipe</th>
                <th className="px-4 py-2.5 text-right">Planned</th><th className="px-4 py-2.5 text-right">Actual</th>
                <th className="px-4 py-2.5 text-right">Var Qty</th><th className="px-4 py-2.5 text-right">Var %</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {rows.map((r, i) => (
                <tr key={i} data-testid={`variance-row-${i}`} className="hover:bg-stone-50/60">
                  <td className="px-4 py-2.5 font-mono text-xs text-stone-600">{r.plan}</td>
                  <td className="px-4 py-2.5 text-stone-600">{r.date}</td>
                  <td className="px-4 py-2.5"><span className="font-medium text-stone-800">{r.productName}</span> <span className="text-[11px] text-stone-400">{r.productId}</span></td>
                  <td className="px-4 py-2.5 text-stone-500">v{String(r.recipeVersion).replace(/^v/i, "")}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNumber(r.planned)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{r.actual == null ? "—" : fmtNumber(r.actual)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${r.varQty > 0 ? "text-sky-600" : r.varQty < 0 ? "text-rose-600" : "text-stone-500"}`}>{r.varQty == null ? "—" : `${r.varQty > 0 ? "+" : ""}${fmtNumber(r.varQty)}`}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${r.varPct > 0 ? "text-sky-600" : r.varPct < 0 ? "text-rose-600" : "text-stone-500"}`}>{r.varPct == null ? "—" : `${r.varPct > 0 ? "+" : ""}${fmtNumber(r.varPct, 1)}%`}</td>
                  <td className={`px-4 py-2.5 font-semibold ${toneFor(r.status)}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
